<template>
  <!-- Figma "G1 · Repair In Progress — Work Checklist" (5689:6371): approved work with a tick per row.
       API (G-11): GET bookings/work-checklist, POST bookings/work-checklist/save. Hidden when the API is not available. -->
  <div v-if="items.length" class="shadow-[0px_0px_10px_0px_#0000001A] rounded-[20px] px-[15px] laptop:px-[20px] py-[20px] bg-white font-roboto">
    <div class="flex flex-wrap items-center justify-between gap-2 pb-[15px]">
      <div class="flex items-center gap-1">
        <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
        <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
        <p class="text-[18px] laptop:text-[20px] font-semibold pl-2 m-0 text-[#333333]">Approved Work</p>
      </div>
      <div class="flex items-center gap-[10px] text-[12px] text-[#5B616B]">
        {{ doneCount }} of {{ items.length }} complete
        <span class="w-[80px] h-[6px] rounded-full bg-[#E9ECF1] overflow-hidden">
          <span class="block h-full rounded-full bg-[#1FA971] transition-all" :style="{ width: (items.length ? doneCount / items.length * 100 : 0) + '%' }" />
        </span>
      </div>
    </div>

    <ul class="list-none m-0 p-0 flex flex-col gap-[8px]">
      <li v-for="item in items" :key="item.key">
        <label class="flex items-center gap-[12px] m-0 px-[12px] py-[10px] rounded-[8px] border-1 cursor-pointer"
          :class="item.is_done ? 'border-[#BFE7D5] bg-[#F0FAF5]' : 'border-[#EFEFEF] bg-white'">
          <input v-model="item.is_done" type="checkbox" class="sr-only">
          <span class="w-[18px] h-[18px] rounded-[4px] flex-shrink-0 flex items-center justify-center border-1"
            :class="item.is_done ? 'bg-[#1FA971] border-[#1FA971]' : 'bg-white border-[#CCCCCC]'">
            <svg v-if="item.is_done" width="11" height="11" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M3 7.4l2.6 2.5L11 4.4" stroke="#FFFFFF" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" /></svg>
          </span>
          <span class="min-w-0">
            <span class="block text-[13px] text-[#333333] break-words">{{ item.title }}</span>
            <span class="block text-[11px] text-[#8A9099]">{{ subtitle(item) }}</span>
          </span>
        </label>
      </li>
    </ul>

    <div class="flex flex-wrap justify-center gap-[10px] mt-[20px]">
      <button type="button" class="flex items-center gap-[6px] h-[36px] px-[16px] rounded-full border-1 border-[#00B7C2] bg-white text-[13px] text-[#00B7C2] disabled:opacity-50"
        :disabled="saving" @click="save()">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="#00B7C2" stroke-width="2" /><path d="M8 12.5l2.5 2.5L16 9.5" stroke="#00B7C2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
        {{ saving ? 'Saving…' : 'Save Progress' }}
      </button>
      <button v-if="canComplete" type="button" class="flex items-center gap-[6px] h-[36px] px-[16px] rounded-full border-1 border-[#7695FF] bg-white text-[13px] text-[#7695FF]"
        @click="$emit('complete')">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="#7695FF" stroke-width="2" /><path d="M12 7v10M9.5 9.5c0-1 1-1.6 2.5-1.6s2.5.7 2.5 1.7-1 1.4-2.5 1.6-2.5.7-2.5 1.8 1 1.7 2.5 1.7 2.5-.6 2.5-1.6" stroke="#7695FF" stroke-width="1.6" stroke-linecap="round" /></svg>
        Complete Work &amp; Collect Payment
      </button>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { useToast } from "vue-toastification";
const toast = useToast();

export default {
  props: {
    bookingId: { type: [String, Number], required: true },
    // shows "Complete Work & Collect Payment" (runs the job's existing End Job action)
    canComplete: { type: Boolean, default: false },
  },
  emits: ['complete'],
  data() {
    return { items: [], saving: false };
  },
  computed: {
    doneCount() {
      return this.items.filter(item => item.is_done).length;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    subtitle(item) {
      if (item.type === 'part') return `Part · Qty ${item.quantity ?? 1}`;
      return [item.option, item.action, item.is_warranty ? 'Labor warranty applies' : ''].filter(Boolean).join(' · ');
    },
    setPayload(body) {
      this.items = Array.isArray(body?.items) ? body.items.map(item => ({ ...item, is_done: !!item.is_done })) : [];
    },
    async load() {
      try {
        const res = await axios.get("bookings/work-checklist", { params: { booking_id: this.bookingId } });
        if (res.data?.status) this.setPayload(res.data.body);
      } catch (e) {
        this.items = [];
      }
    },
    async save() {
      this.saving = true;
      try {
        const res = await axios.post("bookings/work-checklist/save", {
          booking_id: this.bookingId,
          done_keys: this.items.filter(item => item.is_done).map(item => item.key),
        });
        if (res.data?.status) {
          this.setPayload(res.data.body);
          toast.success("Progress saved.");
        } else {
          toast.error(res.data?.message || "Could not save progress.");
        }
      } catch (e) {
        toast.error("Could not save progress.");
      }
      this.saving = false;
    },
  },
};
</script>
